/**
 * AdminVenueSpacesPage: edit room dimensions and exclusion zones (PRD
 * §5.1, §17's action item — venue obstacle data must be entered before
 * the engine is production-accurate). Coordinate-entry form for
 * exclusion zones rather than a drag-to-define-obstacle UI, per the
 * implementation plan's "avoid over-engineering this corner" call.
 */

import { useState } from 'react';
import { useRoomDefinitions, useUpdateRoomDefinition } from '../api/roomDefinitions';
import type { ExclusionZone, RoomDefinition } from '../domain/types';
import { Toast, ToastStack } from '../components/shared/Toast';

export function AdminVenueSpacesPage() {
  const { data: rooms, isLoading } = useRoomDefinitions();
  const [toast, setToast] = useState<{ message: string; tone: 'success' | 'error' } | null>(null);

  if (isLoading || !rooms) {
    return <p className="p-8 font-body text-sm text-ink-400">{'Loading\u2026'}</p>;
  }

  return (
    <div className="mx-auto max-w-4xl px-6 py-8">
      <h1 className="font-display text-2xl text-ink-700">Venue Spaces</h1>
      <p className="mt-1 font-body text-sm text-ink-400">
        Room dimensions should match real measurements exactly. Exclusion zones mark fixed
        features (columns, a built-in bar, a fireplace) that generated and manual layouts must
        never overlap.
      </p>

      <div className="mt-6 space-y-6">
        {rooms.map((room) => (
          <RoomEditor key={room.room} room={room} onSaved={setToast} />
        ))}
      </div>

      <ToastStack>
        {toast && <Toast message={toast.message} tone={toast.tone} onDismiss={() => setToast(null)} />}
      </ToastStack>
    </div>
  );
}

function RoomEditor({
  room,
  onSaved,
}: {
  key?: React.Key;
  room: RoomDefinition;
  onSaved: (toast: { message: string; tone: 'success' | 'error' }) => void;
}) {
  const updateRoom = useUpdateRoomDefinition();
  const [widthFt, setWidthFt] = useState(room.widthFt);
  const [lengthFt, setLengthFt] = useState(room.lengthFt);
  const [zones, setZones] = useState<ExclusionZone[]>(room.exclusionZones);

  const dimensionsDirty = widthFt !== room.widthFt || lengthFt !== room.lengthFt;

  function addZone() {
    setZones((prev) => [...prev, { label: 'New feature', x: 0, y: 0, width: 3, height: 3, rotation: 0 }]);
  }

  function updateZone(index: number, changes: Partial<ExclusionZone>) {
    setZones((prev) => prev.map((z, i) => (i === index ? { ...z, ...changes } : z)));
  }

  function removeZone(index: number) {
    setZones((prev) => prev.filter((_, i) => i !== index));
  }

  function saveDimensions() {
    updateRoom.mutate(
      { room: room.room, widthFt, lengthFt },
      {
        onSuccess: () => onSaved({ message: `${room.displayName} dimensions saved.`, tone: 'success' }),
        onError: () => onSaved({ message: 'Could not save dimensions \u2014 please try again.', tone: 'error' }),
      }
    );
  }

  function saveZones() {
    updateRoom.mutate(
      { room: room.room, exclusionZones: zones },
      {
        onSuccess: () => onSaved({ message: `${room.displayName} fixed features saved.`, tone: 'success' }),
        onError: () => onSaved({ message: 'Could not save fixed features \u2014 please try again.', tone: 'error' }),
      }
    );
  }

  return (
    <section className="rounded-lg border border-ivory-300 bg-white p-5">
      <h2 className="font-display text-lg text-ink-700">{room.displayName}</h2>

      <div className="mt-3 flex items-center gap-3">
        <label className="flex items-center gap-2">
          <span className="font-body text-sm text-ink-500">Width (ft)</span>
          <input
            type="number"
            value={widthFt}
            onChange={(e) => setWidthFt(Number(e.target.value))}
            className="w-20 rounded border border-ivory-300 px-2 py-1 font-body text-sm text-ink-700"
          />
        </label>
        <label className="flex items-center gap-2">
          <span className="font-body text-sm text-ink-500">Length (ft)</span>
          <input
            type="number"
            value={lengthFt}
            onChange={(e) => setLengthFt(Number(e.target.value))}
            className="w-20 rounded border border-ivory-300 px-2 py-1 font-body text-sm text-ink-700"
          />
        </label>
        {dimensionsDirty && (
          <button onClick={saveDimensions} className="font-body text-sm text-evergreen-600 underline">
            Save dimensions
          </button>
        )}
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <p className="font-body text-sm font-medium text-ink-600">Fixed features / exclusion zones</p>
          <button onClick={addZone} className="font-body text-xs text-evergreen-600 underline">
            + Add feature
          </button>
        </div>

        {zones.length === 0 && (
          <p className="mt-2 font-body text-sm text-ink-400">
            No fixed features recorded for this room yet.
          </p>
        )}

        <div className="mt-2 space-y-2">
          {zones.map((zone, i) => (
            <div key={i} className="grid grid-cols-6 items-center gap-2 rounded border border-ivory-200 p-2">
              <input
                value={zone.label}
                onChange={(e) => updateZone(i, { label: e.target.value })}
                placeholder="Label"
                className="col-span-2 rounded border border-ivory-300 px-2 py-1 font-body text-xs text-ink-700"
              />
              <NumberField label="x" value={zone.x} onChange={(v) => updateZone(i, { x: v })} />
              <NumberField label="y" value={zone.y} onChange={(v) => updateZone(i, { y: v })} />
              <NumberField label="w" value={zone.width} onChange={(v) => updateZone(i, { width: v })} />
              <NumberField label="h" value={zone.height} onChange={(v) => updateZone(i, { height: v })} />
              <button
                onClick={() => removeZone(i)}
                className="col-span-6 justify-self-end font-body text-xs text-danger"
              >
                Remove
              </button>
            </div>
          ))}
        </div>

        <button
          onClick={saveZones}
          className="mt-3 rounded bg-evergreen-600 px-4 py-1.5 font-body text-sm text-ivory-50 hover:bg-evergreen-700"
        >
          Save fixed features
        </button>
      </div>
    </section>
  );
}

function NumberField({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label className="flex items-center gap-1">
      <span className="font-body text-xs text-ink-400">{label}</span>
      <input
        type="number"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        className="w-14 rounded border border-ivory-300 px-1 py-1 font-body text-xs text-ink-700"
      />
    </label>
  );
}
